
import React, { Component } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import CachedIcon from '@material-ui/icons/Cached';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import CircularProgress from '@material-ui/core/CircularProgress';
import styled from 'styled-components';
import { grey } from '@material-ui/core/colors';

import { DateDisplay } from 'core/Display';
import { useVehicleDraggable } from './Dnd';
import { getDayBindingSearchParams, getVehicleTractorId, searchBindings } from './changeBinding';


const StyledDate = styled.div`
  font-weight: 500;
  height: 40px;
  padding-left: 7px;
  display: flex;
  align-items: center;

  span.total {
    font-weight: normal;
    margin-left: 10px;
    font-size: 12px;
    color: ${grey[900]};
  }
`;

const StyledTableRow = styled(TableRow)`
  cursor: ${props => props.$disabled ? 'default' : 'grab'};
  opacity: ${props => props.$isDragging ? 0.4 : 1};

  .MuiTableCell-root {
    padding: 6px;
    font-size: 12px;
    vertical-align: top;
  }
`;

const DragHandleCell = styled(TableCell)`
  width: 28px;
  min-width: 28px !important;
  max-width: 28px;
  color: #bdbdbd;
  vertical-align: middle !important;
`;

const VehicleNumber = styled.div`
  font-weight: 600;
  white-space: nowrap;
`;

const VehicleMeta = styled.div`
  color: #828282;
  margin-top: 3px;
`;


function DraggableVehicleRow({ vehicle, disabled, children }) {
  const { attributes, listeners, setNodeRef, isDragging } = useVehicleDraggable(vehicle, disabled);

  return (
    <StyledTableRow
      ref={setNodeRef}
      $isDragging={isDragging}
      $disabled={disabled}
      {...listeners}
      {...attributes}
    >
      {children}
    </StyledTableRow>
  );
}


export default class PlanningFreeVehicles extends Component {

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      items: [],
    };
  }

  componentDidMount() {
    this.loadItems();
  }

  componentDidUpdate(prevProps) {
    if (this.props.date && !this.props.date.isSame(prevProps.date, 'day')) {
      this.loadItems();
    }
  }

  loadItems = () => {
    const { date } = this.props;
    if (!date) {
      return;
    }

    this.setState({ loading: true });
    searchBindings(getDayBindingSearchParams(date)).then(results => {
      const items = results.filter(item => {
        const tractor = item.start_slot && item.start_slot.tractor_unit;
        return tractor && tractor.is_active !== false;
      }).map(item => ({
        ...item,
        detail: item.start_slot.tractor_unit,
      }));
      items.sort((a, b) => {
        const left = (a.detail.number || '').toString();
        const right = (b.detail.number || '').toString();
        return left.localeCompare(right, 'ru');
      });
      this.setState({
        loading: false,
        items,
      });
    }).catch(() => {
      this.setState({
        loading: false,
        items: [],
      });
    });
  };

  renderVehicleLabel = (detail) => {
    const typeLabel = detail.type && detail.type.label
      ? detail.type.label
      : [detail.brand, detail.model].filter(Boolean).join(' ');
    return [typeLabel, detail.year ? `(${detail.year})` : null].filter(Boolean).join(' ');
  };

  getVisibleItems = () => {
    const { excludeVehicleIds = [] } = this.props;
    const { items } = this.state;
    if (!excludeVehicleIds.length) {
      return items;
    }
    return items.filter(item => !excludeVehicleIds.includes(getVehicleTractorId(item)));
  };

  render() {
    const { date, disabled } = this.props;
    const { loading } = this.state;
    const items = this.getVisibleItems();

    return (
      <React.Fragment>
        <StyledDate>
          Свободные ТС
          {date && (
            <span className={'total'}>
              на <DateDisplay value={date} format={'DD MMMM'} />
            </span>
          )}
          <span className={'total'}>Всего: {items.length}</span>
          <IconButton
            disabled={loading}
            size={'small'}
            className={'ml-10'}
            onClick={this.loadItems}
          >
            <CachedIcon />
          </IconButton>
        </StyledDate>

        {loading && items.length === 0 && (
          <CircularProgress size={24} />
        )}

        {!loading && items.length === 0 && (
          <div style={{padding: '10px 7px', fontSize: '12px', color: '#828282'}}>
            Нет свободных ТС на этот день
          </div>
        )}

        {items.length > 0 && (
          <TableContainer>
            <Paper elevation={3} variant="outlined">
              <Table aria-label="free vehicles">
                <TableBody>
                  {items.map(item => {
                    const slot = item.start_slot;
                    const detail = item.detail;
                    return (
                      <DraggableVehicleRow
                        key={slot.id}
                        vehicle={item}
                        disabled={disabled}
                      >
                        <DragHandleCell>
                          <DragIndicatorIcon fontSize="small" />
                        </DragHandleCell>
                        <TableCell>
                          <VehicleNumber>{detail.number_display || detail.number || '—'}</VehicleNumber>
                          <VehicleMeta>{this.renderVehicleLabel(detail) || '—'}</VehicleMeta>
                          <VehicleMeta>
                            {slot.driver ? slot.driver.short_name : 'Без водителя'}
                            {slot.semi_trailer ? `, ${slot.semi_trailer.number_display || slot.semi_trailer.number}` : ''}
                          </VehicleMeta>
                        </TableCell>
                      </DraggableVehicleRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </TableContainer>
        )}
      </React.Fragment>
    );
  }
}
